<template>
  <div class="user-list-container mt-5 pt-5">
    <div class="container pt-5 px-3">
      <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h1 class="section-title">Gestión de Usuarios</h1>
        <div class="d-flex">
          <button class="btn btn-success me-2" @click="openCreateUserModal">
            <i class="fas fa-user-plus me-1"></i> Crear Usuario
          </button>
          <button class="btn btn-outline-secondary me-2" @click="refreshUsers">
            <i class="fas fa-sync-alt me-1"></i> Actualizar
          </button>
        </div>
      </div>

      <!-- Filtros -->
      <div class="card filter-card mb-4">
        <div class="card-body">
          <div class="row">
            <div class="col-md-6 col-sm-12 mb-3 mb-md-0">
              <div class="form-group">
                <label>Buscar usuarios</label>
                <div class="input-group">
                  <span class="input-group-text bg-dark border-dark text-light">
                    <i class="fas fa-search"></i>
                  </span>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="filters.username"
                    placeholder="Buscar por nombre, apellido o nombre de usuario"
                    @keyup.enter="applyFilters"
                  >
                </div>
                <small class="text-muted">Puedes buscar por nombre, apellido o nombre de usuario</small>
              </div>
            </div>
            <div class="col-md-3 col-sm-12 mb-3 mb-md-0">
              <div class="form-group">
                <label>Email</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="filters.email"
                  placeholder="Buscar por email"
                  @keyup.enter="applyFilters"
                >
              </div>
            </div>
            <div class="col-md-3 col-sm-12 mb-3 mb-md-0">
              <div class="form-group">
                <label>Estado del usuario</label>
                <select 
                  class="form-select" 
                  v-model="filters.status"
                >
                  <option value="">Todos los estados</option>
                  <option value="active">Activos</option>
                  <option value="inactive">Inactivos</option>
                </select>
              </div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-md-12 col-sm-12 mb-3 mb-md-0 d-flex align-items-end">
              <div class="w-100 d-flex justify-content-end gap-2">
                <button class="btn btn-secondary" @click="refreshUsers">
                  <i class="fas fa-times me-1"></i> Limpiar filtros
                </button>
                <button class="btn btn-primary" @click="applyFilters" :disabled="loading">
                  <i class="fas fa-filter me-2"></i> 
                  <span v-if="loading">Aplicando...</span>
                  <span v-else>Aplicar filtros</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loader -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
        <p class="mt-2">Cargando usuarios...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="alert alert-danger">
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ error }}
      </div>

      <!-- Lista de usuarios -->
      <div v-else-if="users.length" class="user-grid">
        <div v-for="user in users" :key="user.id" class="user-card">
          <div class="user-card-header">
            <div class="user-avatar">
              <i class="fas fa-user"></i>
            </div>
            <div class="user-info">
              <h5 class="user-name">{{ user.first_name }} {{ user.last_name }}</h5>
              <p class="user-username">@{{ user.username }}</p>
            </div>
            <div class="user-badges">
              <div class="user-badge" :class="{'admin-badge': user.is_staff}">
                {{ user.is_staff ? 'Admin' : 'Usuario' }}
              </div>
              <div class="user-badge status-badge" 
                   :class="{'active-badge': user.is_active, 'inactive-badge': !user.is_active}"
                   @click="toggleUserStatus(user)"
                   :title="`Click para ${user.is_active ? 'desactivar' : 'activar'} usuario`"
                   style="cursor: pointer;">
                <i :class="user.is_active ? 'fas fa-check-circle' : 'fas fa-times-circle'" class="me-1"></i>
                {{ user.is_active ? 'Activo' : 'Inactivo' }}
              </div>
            </div>
          </div>
          <div class="user-card-body">
            <div class="user-detail">
              <i class="fas fa-envelope"></i>
              <span>{{ user.email }}</span>
            </div>
            <div class="user-detail" v-if="user.instagram">
              <i class="fab fa-instagram"></i>
              <span>{{ user.instagram }}</span>
            </div>
            <div class="user-detail">
              <i class="fas fa-calendar-alt"></i>
              <span>ID: {{ user.id }}</span>
            </div>
          </div>
          <div class="user-card-footer">
            <router-link :to="`/admin/usuarios/${user.id}`" class="btn btn-primary btn-sm me-2">
              <i class="fas fa-eye me-1"></i> Ver detalles
            </router-link>
            <button 
              class="btn btn-sm me-2"
              :class="user.is_active ? 'btn-warning' : 'btn-success'"
              @click="toggleUserStatus(user)"
              :disabled="user.id === currentUserId"
              :title="user.id === currentUserId ? 'No puedes modificar tu propia cuenta' : (user.is_active ? 'Desactivar usuario' : 'Activar usuario')"
            >
              <i :class="user.is_active ? 'fas fa-user-slash' : 'fas fa-user-check'" class="me-1"></i>
              {{ user.is_active ? 'Desactivar' : 'Activar' }}
            </button>
            <button 
              class="btn btn-danger btn-sm"
              @click="confirmDeleteUser(user)"
              :disabled="user.id === currentUserId"
              :title="user.id === currentUserId ? 'No puedes eliminar tu propia cuenta' : 'Eliminar usuario'"
            >
              <i class="fas fa-trash me-1"></i> Eliminar
            </button>
          </div>
        </div>
      </div>

      <!-- Sin resultados -->
      <div v-else class="empty-state">
        <i class="fas fa-users fa-3x mb-3"></i>
        <h3>No se encontraron usuarios</h3>
        <p>Intenta con otros filtros de búsqueda o añade nuevos usuarios al sistema.</p>
      </div>

      <!-- Paginación -->
      <div v-if="pagination.count > 0" class="pagination-container mt-4">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="pagination-info">
            Mostrando {{ paginationInfo }}
          </div>
          <nav aria-label="Navegación de páginas">
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
                <button class="page-link" @click="goToPage(1)" :disabled="pagination.currentPage === 1">
                  <i class="fas fa-angle-double-left"></i>
                </button>
              </li>
              <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
                <button class="page-link" @click="goToPage(pagination.currentPage - 1)" :disabled="pagination.currentPage === 1">
                  <i class="fas fa-angle-left"></i>
                </button>
              </li>
              
              <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: pagination.currentPage === page }">
                <button class="page-link" @click="goToPage(page)">{{ page }}</button>
              </li>
              
              <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.totalPages }">
                <button class="page-link" @click="goToPage(pagination.currentPage + 1)" :disabled="pagination.currentPage === pagination.totalPages">
                  <i class="fas fa-angle-right"></i>
                </button>
              </li>
              <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.totalPages }">
                <button class="page-link" @click="goToPage(pagination.totalPages)" :disabled="pagination.currentPage === pagination.totalPages">
                  <i class="fas fa-angle-double-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <!-- Modal de confirmación -->
      <div class="modal fade" id="deleteUserModal" tabindex="-1" aria-labelledby="deleteUserModalLabel" aria-hidden="true">
        <div class="modal-dialog">
          <div class="modal-content bg-dark text-light">
            <div class="modal-header border-secondary">
              <h5 class="modal-title" id="deleteUserModalLabel">Confirmar eliminación</h5>
              <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div v-if="userToDelete">
                <p class="mb-3">¿Estás seguro que deseas eliminar al usuario?</p>
                <div class="user-info-modal p-3 bg-secondary rounded">
                  <strong>{{ userToDelete.first_name }} {{ userToDelete.last_name }}</strong><br>
                  <small class="text-muted">@{{ userToDelete.username }}</small><br>
                  <small class="text-muted">{{ userToDelete.email }}</small>
                </div>
                <div class="alert alert-warning mt-3">
                  <i class="fas fa-exclamation-triangle me-2"></i>
                  Esta acción no se puede deshacer. Se eliminarán todos los datos asociados al usuario.
                </div>
              </div>
            </div>
            <div class="modal-footer border-secondary">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
              <button 
                type="button" 
                class="btn btn-danger" 
                @click="deleteUser"
                :disabled="isDeleting"
              >
                <span v-if="isDeleting">
                  <i class="fas fa-spinner fa-spin me-2"></i>Eliminando...
                </span>
                <span v-else>
                  <i class="fas fa-trash me-2"></i>Eliminar
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal de creación de usuario -->
      <div class="modal fade py-5 mt-3" id="createUserModal" tabindex="-1" aria-labelledby="createUserModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg">
          <div class="modal-content bg-dark text-light">
            <div class="modal-header border-secondary">
              <h5 class="modal-title fs-5" id="createUserModalLabel">Crear Nuevo Usuario</h5>
              <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="createUser">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="firstName" class="form-label">Nombre *</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="firstName"
                      v-model="newUser.first_name"
                      :class="{ 'is-invalid': createUserErrors.first_name }"
                      required
                    >
                    <div v-if="createUserErrors.first_name" class="invalid-feedback">
                      {{ createUserErrors.first_name }}
                    </div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="lastName" class="form-label">Apellido *</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="lastName"
                      v-model="newUser.last_name"
                      :class="{ 'is-invalid': createUserErrors.last_name }"
                      required
                    >
                    <div v-if="createUserErrors.last_name" class="invalid-feedback">
                      {{ createUserErrors.last_name }}
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="username" class="form-label">Nombre de Usuario *</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="username"
                      v-model="newUser.username"
                      :class="{ 'is-invalid': createUserErrors.username }"
                      required
                    >
                    <div v-if="createUserErrors.username" class="invalid-feedback">
                      {{ createUserErrors.username }}
                    </div>
                    <small class="text-muted">Solo letras, números y guiones bajos. Mínimo 3 caracteres.</small>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="email" class="form-label">Email *</label>
                    <input 
                      type="email" 
                      class="form-control" 
                      id="email"
                      v-model="newUser.email"
                      :class="{ 'is-invalid': createUserErrors.email }"
                      required
                    >
                    <div v-if="createUserErrors.email" class="invalid-feedback">
                      {{ createUserErrors.email }}
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="password" class="form-label">Contraseña *</label>
                    <input 
                      type="password" 
                      class="form-control" 
                      id="password"
                      v-model="newUser.password"
                      :class="{ 'is-invalid': createUserErrors.password }"
                      required
                    >
                    <div v-if="createUserErrors.password" class="invalid-feedback">
                      <div v-if="Array.isArray(createUserErrors.password)">
                        <div v-for="error in createUserErrors.password" :key="error">{{ error }}</div>
                      </div>
                      <div v-else>{{ createUserErrors.password }}</div>
                    </div>
                    <small class="text-muted">Mínimo 8 caracteres, debe incluir letras y números.</small>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="password2" class="form-label">Confirmar Contraseña *</label>
                    <input 
                      type="password" 
                      class="form-control" 
                      id="password2"
                      v-model="newUser.password2"
                      :class="{ 'is-invalid': createUserErrors.password2 }"
                      required
                    >
                    <div v-if="createUserErrors.password2" class="invalid-feedback">
                      {{ createUserErrors.password2 }}
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12 mb-3">
                    <label for="instagram" class="form-label">Instagram (opcional)</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="instagram"
                      v-model="newUser.instagram"
                      placeholder="@usuario_instagram"
                    >
                    <small class="text-muted">Opcional. Puedes incluir o no el símbolo @.</small>
                  </div>
                </div>
                <div v-if="createUserError" class="alert alert-danger">
                  <i class="fas fa-exclamation-triangle me-2"></i>
                  <div v-if="Array.isArray(createUserError)">
                    <div v-for="error in createUserError" :key="error">{{ error }}</div>
                  </div>
                  <div v-else>{{ createUserError }}</div>
                </div>
              </form>
            </div>
            <div class="modal-footer border-secondary">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
              <button 
                type="button" 
                class="btn btn-success" 
                @click="createUser"
                :disabled="isCreatingUser"
              >
                <span v-if="isCreatingUser">
                  <i class="fas fa-spinner fa-spin me-2"></i>Creando...
                </span>
                <span v-else>
                  <i class="fas fa-user-plus me-2"></i>Crear Usuario
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useToast } from 'vue-toastification';
import { getUsers, deleteUser as deleteUserService, toggleUserStatus as toggleUserStatusService, createUserAsAdmin } from '@/services/auth';

const toast = useToast();
const users = ref([]);
const loading = ref(true);
const error = ref(null);
const filters = ref({
  username: '',
  email: '',
  status: '', // active, inactive, o vacío para todos
  userType: '', // admin, user, o vacío para todos
  page: 1,
  page_size: 12
});

const pagination = ref({
  count: 0,
  currentPage: 1,
  totalPages: 1,
  next: null,
  previous: null
});

// Variables para eliminación de usuarios
const userToDelete = ref(null);
const isDeleting = ref(false);
const currentUserId = ref(parseInt(localStorage.getItem('userId')));

// Variables para creación de usuarios
const newUser = ref({
  first_name: '',
  last_name: '',
  username: '',
  email: '',
  password: '',
  password2: '',
  instagram: ''
});
const isCreatingUser = ref(false);
const createUserError = ref(null);
const createUserErrors = ref({});

// Información de paginación para mostrar
const paginationInfo = computed(() => {
  const start = (pagination.value.currentPage - 1) * filters.value.page_size + 1;
  const end = Math.min(start + users.value.length - 1, pagination.value.count);
  return `${start}-${end} de ${pagination.value.count} usuarios`;
});

// Cálculo de páginas visibles para la paginación
const visiblePages = computed(() => {
  const totalPages = pagination.value.totalPages;
  const currentPage = pagination.value.currentPage;
  
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  
  if (currentPage <= 3) {
    return [1, 2, 3, 4, 5, '...', totalPages];
  }
  
  if (currentPage >= totalPages - 2) {
    return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  
  return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
});


// Nueva función para aplicar filtros al hacer click en el botón
const applyFilters = () => {
  filters.value.page = 1; // Resetear a la primera página al aplicar filtros
  searchUsers();
};

const searchUsers = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    // Construir parámetros de consulta incluyendo todos los filtros
    const queryParams = {
      page: filters.value.page,
      page_size: filters.value.page_size
    };
    
    // Agregar filtros solo si tienen valor
    if (filters.value.username.trim()) {
      queryParams.username = filters.value.username.trim();
    }
    
    if (filters.value.email.trim()) {
      queryParams.email = filters.value.email.trim();
    }
    
    if (filters.value.status) {
      queryParams.status = filters.value.status;
    }
    
    if (filters.value.userType) {
      queryParams.user_type = filters.value.userType;
    }
    
    console.log('Enviando parámetros a la API:', queryParams); // Debug
    
    const response = await getUsers(queryParams);
    
    console.log('Respuesta de la API:', response); // Debug
    
    // Extraer datos de usuarios y metadata de paginación directamente del backend
    users.value = response.results || [];
    
    // Actualizar información de paginación
    pagination.value = {
      count: response.count || 0,
      currentPage: filters.value.page,
      totalPages: Math.ceil((response.count || 0) / filters.value.page_size),
      next: response.next,
      previous: response.previous
    };
    
    if (users.value.length === 0 && pagination.value.count === 0) {
      toast.info('No se encontraron usuarios con los filtros aplicados');
    }
  } catch (err) {
    console.error('Error al buscar usuarios:', err);
    error.value = 'Error al cargar los usuarios. Por favor, inténtalo de nuevo.';
    toast.error(error.value);
  } finally {
    loading.value = false;
  }
};

const refreshUsers = () => {
  filters.value.username = '';
  filters.value.email = '';
  filters.value.status = '';
  filters.value.userType = '';
  filters.value.page = 1;
  applyFilters(); // Usar applyFilters en lugar de searchUsers directamente
};

const goToPage = (page) => {
  if (page === '...') return;
  
  console.log('Navegando a página:', page); // Debug
  filters.value.page = page;
  searchUsers(); // Cambiar a searchUsers() para mantener la página
  
  // Scroll al inicio de la lista
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

const confirmDeleteUser = (user) => {
  userToDelete.value = user;
  const modal = new window.bootstrap.Modal(document.getElementById('deleteUserModal'));
  modal.show();
};

const deleteUser = async () => {
  if (!userToDelete.value) return;
  
  isDeleting.value = true;
  try {
    await deleteUserService(userToDelete.value.id);
    toast.success(`Usuario ${userToDelete.value.username} eliminado correctamente`);
    
    // Cerrar modal
    const modal = window.bootstrap.Modal.getInstance(document.getElementById('deleteUserModal'));
    modal.hide();
    
    // Recargar la lista de usuarios
    await searchUsers();
    
  } catch (error) {
    console.error('Error al eliminar usuario:', error);
    const errorMessage = error.response?.data?.error || 'Error al eliminar el usuario';
    toast.error(errorMessage);
  } finally {
    isDeleting.value = false;
    userToDelete.value = null;
  }
};

const toggleUserStatus = async (user) => {
  // Prevenir cambiar el estado de la propia cuenta
  if (user.id === currentUserId.value) {
    toast.warning('No puedes modificar tu propia cuenta');
    return;
  }
  
  try {
    const newStatus = !user.is_active;
    await toggleUserStatusService(user.id, newStatus);
    
    // Actualizar el estado localmente
    user.is_active = newStatus;
    
    const statusText = newStatus ? 'activado' : 'desactivado';
    toast.success(`Usuario ${user.username} ${statusText} correctamente`);
    
  } catch (error) {
    console.error('Error al cambiar estado del usuario:', error);
    const errorMessage = error.response?.data?.error || 'Error al cambiar el estado del usuario';
    toast.error(errorMessage);
  }
};

// Funciones para creación de usuarios
const openCreateUserModal = () => {
  // Limpiar formulario y errores
  newUser.value = {
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    password: '',
    password2: '',
    instagram: ''
  };
  createUserError.value = null;
  createUserErrors.value = {};
  
  // Abrir modal
  const modal = new window.bootstrap.Modal(document.getElementById('createUserModal'));
  modal.show();
};

const createUser = async () => {
  isCreatingUser.value = true;
  createUserError.value = null;
  createUserErrors.value = {};
  
  try {
    // Validar que las contraseñas coincidan
    if (newUser.value.password !== newUser.value.password2) {
      createUserErrors.value.password2 = 'Las contraseñas no coinciden';
      isCreatingUser.value = false;
      return;
    }
    
    // Preparar datos del usuario
    const userData = {
      first_name: newUser.value.first_name.trim(),
      last_name: newUser.value.last_name.trim(),
      username: newUser.value.username.trim(),
      email: newUser.value.email.trim().toLowerCase(),
      password: newUser.value.password,
      password2: newUser.value.password2,
      instagram: newUser.value.instagram.trim() || null
    };
    
    // Registrar usuario como admin (activo automáticamente)
    await createUserAsAdmin(userData);
    
    toast.success(`Usuario ${userData.username} creado correctamente`);
    
    // Cerrar modal
    const modal = window.bootstrap.Modal.getInstance(document.getElementById('createUserModal'));
    modal.hide();
    
    // Recargar la lista de usuarios
    await applyFilters();
    
  } catch (error) {
    console.error('Error al crear usuario:', error);
    
    // Manejar errores de validación del backend
    if (error.errors && Array.isArray(error.errors)) {
      createUserError.value = error.errors;
    } else if (error.response?.data?.error) {
      if (Array.isArray(error.response.data.error)) {
        createUserError.value = error.response.data.error;
      } else {
        createUserError.value = error.response.data.error;
      }
    } else if (error.message) {
      createUserError.value = error.message;
    } else {
      createUserError.value = 'Error al crear el usuario. Por favor, inténtalo de nuevo.';
    }
  } finally {
    isCreatingUser.value = false;
  }
};

onMounted(async () => {
  await applyFilters(); // Usar applyFilters para la carga inicial
});
</script>

<style scoped>
.user-list-container {
  min-height: 100vh;
  background: var(--black-color);
  padding-bottom: 3rem;
}

.section-title {
  color: var(--white-color);
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.filter-card {
  background: var(--black-color-3);
  border: none;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  color: var(--white-color);
  margin-bottom: 0.5rem;
  display: block;
}

.form-control {
  background: var(--black-color-2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--white-color);
}

.form-control:focus {
  background: var(--black-color-2);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: none;
}

.input-group-text {
  border-right: none;
}

.input-group .form-control {
  border-left: none;
}

.input-group .form-control:focus {
  border-left: none;
  border-color: var(--primary-color);
}

.text-muted {
  color: rgba(255, 255, 255, 0.5) !important;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.user-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.user-card {
  background: var(--black-color-3);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.user-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.user-card-header {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  position: relative;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.user-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: white;
  font-size: 1.5rem;
}

.user-info {
  flex: 1;
}

.user-name {
  color: var(--white-color);
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.user-username {
  color: var(--white-color-2);
  margin: 0;
  font-size: 0.9rem;
}

.user-badges {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
}

.user-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(108, 117, 125, 0.2);
  color: #6c757d;
  white-space: nowrap;
}

.admin-badge {
  background: rgba(var(--primary-color-rgb), 0.2);
  color: var(--primary-color);
}

.status-badge.active-badge {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
  transition: all 0.3s ease;
}

.status-badge.inactive-badge {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
  transition: all 0.3s ease;
}

.status-badge:hover {
  transform: scale(1.05);
  opacity: 0.8;
}

.status-badge.active-badge:hover {
  background: rgba(40, 167, 69, 0.3);
}

.status-badge.inactive-badge:hover {
  background: rgba(220, 53, 69, 0.3);
}

.user-card-body {
  padding: 1rem 1.5rem;
}

.user-detail {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  color: var(--white-color-2);
}

.user-detail i {
  width: 20px;
  margin-right: 0.75rem;
  color: var(--white-color-2);
}

.user-card-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.user-card-footer .btn {
  min-width: 100px;
  flex: 1;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--white-color-2);
  text-align: center;
}

/* Estilos para la paginación */
.pagination-container {
  color: var(--white-color);
}

.pagination-info {
  color: var(--white-color-2);
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.pagination {
  margin-bottom: 0;
}

.page-link {
  background-color: var(--black-color-3);
  border-color: rgba(255, 255, 255, 0.1);
  color: var(--white-color);
}

.page-item.active .page-link {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
}

.page-item.disabled .page-link {
  background-color: var(--black-color-3);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.3);
}

.page-link:hover:not(.disabled) {
  background-color: var(--black-color-2);
  border-color: rgba(255, 255, 255, 0.2);
  color: var(--white-color);
}

/* Media queries para dispositivos móviles */
@media (max-width: 767px) {
  .section-title {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    width: 100%;
  }
  
  .user-grid {
    grid-template-columns: 1fr;
  }
  
  .user-card-header {
    padding: 1rem;
  }
  
  .user-badges {
    position: static;
    margin-top: 0.5rem;
    flex-direction: row;
    justify-content: flex-start;
    gap: 0.5rem;
  }
  
  .user-badge {
    display: inline-block;
    font-size: 0.75rem;
  }
  
  .user-detail {
    word-break: break-word;
  }
  
  .user-detail span {
    font-size: 0.9rem;
  }
  
  .user-card-footer {
    padding: 1rem;
  }
  
  .user-card-footer .btn {
    min-width: 80px;
    font-size: 0.8rem;
    padding: 0.4rem 0.6rem;
  }
  
  .user-card-body {
    padding: 1rem;
  }
  
  .pagination {
    flex-wrap: wrap;
    justify-content: center;
  }

  /* Estilos específicos para los filtros */
  .filter-card {
    background: var(--card-bg-color, #1a1a1a) !important;
    border: 1px solid var(--border-color, #333) !important;
  }

  .filter-card .form-label {
    color: var(--text-color, #fff);
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  .filter-card .form-control,
  .filter-card .form-select {
    background-color: var(--input-bg-color, #2a2a2a);
    border: 1px solid var(--border-color, #444);
    color: var(--text-color, #fff);
  }

  .filter-card .form-control:focus,
  .filter-card .form-select:focus {
    background-color: var(--input-focus-bg-color, #333);
    border-color: var(--primary-color, #007bff);
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    color: var(--text-color, #fff);
  }

  .filter-card .form-select option {
    background-color: var(--input-bg-color, #2a2a2a);
    color: var(--text-color, #fff);
  }

  .gap-2 {
    gap: 0.5rem !important;
  }
  
  .pagination-info {
    width: 100%;
    text-align: center;
    margin-bottom: 1rem;
  }
}

/* Estilos para el modal */
.user-info-modal {
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-content.bg-dark {
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Estilos para el modal de creación de usuario */
#createUserModal .form-control {
  background: var(--black-color-2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--white-color);
}

#createUserModal .form-control:focus {
  background: var(--black-color-2);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: 0 0 0 0.2rem rgba(var(--primary-color-rgb), 0.25);
}

#createUserModal .form-label {
  color: var(--white-color);
  font-weight: 500;
  margin-bottom: 0.5rem;
}

#createUserModal .text-muted {
  color: rgba(255, 255, 255, 0.5) !important;
  font-size: 0.8rem;
}

#createUserModal .is-invalid {
  border-color: #dc3545;
}

#createUserModal .invalid-feedback {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

#createUserModal .alert {
  border: none;
  border-radius: 8px;
}

#createUserModal .btn-success {
  background-color: var(--success-color, #28a745);
  border-color: var(--success-color, #28a745);
}

#createUserModal .btn-success:hover {
  background-color: var(--success-color-hover, #218838);
  border-color: var(--success-color-hover, #218838);
}
</style> 